import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import BaseMapbox from '../../../../src/scripts/classes/BaseMapbox';

/**
 * MapStandard Component
 * Initializes and manages a Mapbox map with markers and popups
 * 
 * @constructor
 * @param {Object} options - Configuration options
 * @param {jQuery} options.$el - jQuery element containing the component
 * @param {string} options.baseClass - Base CSS class name
 * @param {string} options.accessToken - Mapbox access token
 * @param {string} options.style - Mapbox style URL
 * @param {Array<number>} options.center - Initial map center [lng, lat]
 * @param {number} options.zoom - Initial zoom level
 */
var MapStandard = function(options) {

	this.options = {
		$el: false,
		baseClass: 'mol-MapStandard',
		accessToken: 'pk.eyJ1IjoiYXV0aGVudGljZmYiLCJhIjoiUmtjWTY2byJ9.QeHZuWdaMRJlobq_RgXngw',
		style: 'mapbox://styles/authenticff/cmfzw19aj005e01s82akoh8mi',
		center: [-77.037855, 38.891725],
		zoom: 15.19
	};

	$.extend(true, this.options, options);

	this.map = null;
	this.$mapContainer = null;
	this.mapItems = [];
	this.markers = [];
	this.geoJsonSource = 'map-standard-points';
	this.geoJsonLayer = 'map-standard-points-layer';

};

/**
 * Initialize the map component
 * Sets up the Mapbox map and loads map items with coordinates
 */
MapStandard.prototype.init = function() {

	var self = this;

	// Find the map container - use the actual embed container, not the wrapper
	this.$mapContainer = this.options.$el.find('.mapbox_code-embed');

	if (!this.$mapContainer.length) {
		console.warn('MapStandard: Map container not found');
		return;
	}

	// Parse map items from data attribute
	this.mapItems = BaseMapbox.parseMapItems(this.options.$el, 'MapStandard');

	// Set access token
	mapboxgl.accessToken = this.options.accessToken;

	// Calculate initial center and zoom from items with coordinates
	var initialBounds = BaseMapbox.calculateInitialCenterAndZoom(
		this.mapItems,
		this.options.center,
		this.options.zoom
	);
	var initialCenter = initialBounds.center;
	var initialZoom = initialBounds.zoom;

	// Initialize the map (will fit bounds after all markers are added)
	this.map = new mapboxgl.Map({
		container: this.$mapContainer[0],
		style: this.options.style,
		center: initialCenter,
		zoom: initialZoom
	});

	// Ensure map container has explicit dimensions for proper zoom center calculation
	// This is critical when using aspect-ratio or flexbox layouts
	setTimeout(function() {
		if (self.map) {
			self.map.resize();
		}
	}, 0);

	// Setup map error handling
	BaseMapbox.setupMapErrorHandling(this.map, this.removeProblematicLayer.bind(this), 'MapStandard');

	// Add navigation controls (optional)
	this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');

	// Ensure map has correct dimensions before initializing
	// This fixes zoom center calculation issues with aspect-ratio/flexbox layouts
	this.map.once('load', function() {
		// Resize map to ensure it has correct dimensions
		self.map.resize();
		
		// Small delay to ensure resize is complete
		setTimeout(function() {
			if (self.mapItems && self.mapItems.length > 0) {
				self.addMarkersWithGeoJson();
			}
		}, 50);
	});

	// Setup window resize handler
	this._resizeCleanup = BaseMapbox.setupMapResize(this.map);

};

/**
 * Remove a problematic layer from the map style
 * Wrapper for BaseMapbox.removeProblematicLayer
 * 
 * @param {string} layerId - The ID of the layer to remove
 */
MapStandard.prototype.removeProblematicLayer = function(layerId) {
	BaseMapbox.removeProblematicLayer(this.map, layerId, 'MapStandard');
};

/**
 * Add markers to the map using GeoJSON source
 * Expects all items to have coordinates already
 * Fits map bounds after all markers are added
 */
MapStandard.prototype.addMarkersWithGeoJson = function() {

	var self = this;
	
	if (!this.mapItems || this.mapItems.length === 0) {
		return;
	}

	// Build GeoJSON FeatureCollection from map items
	var features = [];
	this.mapItems.forEach(function(item, index) {
		if (!BaseMapbox.isValidCoordinate(item.coordinates)) {
			console.warn('MapStandard: Item missing valid coordinates', item);
			return;
		}

		// Create GeoJSON feature with properties for popup
		// Store button as JSON string if it's an object to preserve structure
		var buttonData = null;
		if (item.button) {
			buttonData = typeof item.button === 'object' ? JSON.stringify(item.button) : item.button;
		}

		var feature = {
			type: 'Feature',
			geometry: {
				type: 'Point',
				coordinates: item.coordinates
			},
			properties: {
				index: index,
				popupHtml: item.popupHtml || '',
				heading: item.heading || '',
				address: item.address || '',
				button: buttonData
			}
		};

		features.push(feature);
	});

	if (features.length === 0) {
		console.warn('MapStandard: No valid features to add');
		return;
	}

	// Create GeoJSON object
	var geoJsonData = {
		type: 'FeatureCollection',
		features: features
	};

	// Add GeoJSON source to map
	if (this.map.getSource(this.geoJsonSource)) {
		// Update existing source
		this.map.getSource(this.geoJsonSource).setData(geoJsonData);
	} else {
		// Add new source
		this.map.addSource(this.geoJsonSource, {
			type: 'geojson',
			data: geoJsonData
		});

		// Add circle layer for markers
		this.map.addLayer({
			id: this.geoJsonLayer,
			type: 'circle',
			source: this.geoJsonSource,
			paint: {
				'circle-radius': 15,
				'circle-color': '#000',
				'circle-stroke-width': 3,
				'circle-stroke-color': '#fff'
			}
		});

		// Add click handler for markers
		this.map.on('click', this.geoJsonLayer, function(e) {
			var feature = e.features[0];
			if (feature && feature.properties) {
				self.showPopupForFeature(feature);
			}
		});

		// Change cursor on hover
		this.map.on('mouseenter', this.geoJsonLayer, function() {
			self.map.getCanvas().style.cursor = 'pointer';
		});

		this.map.on('mouseleave', this.geoJsonLayer, function() {
			self.map.getCanvas().style.cursor = '';
		});
	}

	// Fit bounds after a brief delay to ensure layer is rendered
	setTimeout(function() {
		self.fitMapToGeoJsonBounds();
	}, 100);

};

/**
 * Show popup for a GeoJSON feature
 * 
 * @param {Object} feature - GeoJSON feature with properties
 */
MapStandard.prototype.showPopupForFeature = function(feature) {

	var properties = feature.properties;
	var coordinates = feature.geometry.coordinates;

	// Use pre-rendered popup HTML (should always be defined)
	var popupContent = properties.popupHtml || '';
	
	if (!popupContent) {
		console.warn('MapStandard: No popupHtml defined for feature', feature);
		return;
	}

	// Check map container height and hide images if less than 765px
	var containerHeight = this.$mapContainer.height();
	if (containerHeight < 765) {
		// Add style tag to hide images in the popup
		var styleTag = '<style>.popup-item_image-wrapper { display: none !important; }</style>';
		popupContent = styleTag + popupContent;
	}

	// Center map on the marker using flyTo (same settings as MapPoi)
	this.map.flyTo({
		center: coordinates,
		zoom: 15,
		duration: 1000
	});

	// Create and show popup
	var popup = new mapboxgl.Popup({ 
		offset: 0,
		closeButton: false 
	})
	.setLngLat(coordinates)
	.setHTML(popupContent)
	.addTo(this.map);

};

/**
 * Fit map bounds to show all GeoJSON features
 * Centers the map view to include all markers with appropriate padding
 * Handles single marker case by centering with a fixed zoom level
 */
MapStandard.prototype.fitMapToGeoJsonBounds = function() {

	var source = this.map.getSource(this.geoJsonSource);
	if (!source || !source._data || !source._data.features || source._data.features.length === 0) {
		console.warn('MapStandard: No GeoJSON features to fit bounds to');
		return;
	}

	var features = source._data.features;

	// Extract coordinates from features
	var coordinates = features
		.filter(function(feature) {
			return feature.geometry && feature.geometry.coordinates;
		})
		.map(function(feature) {
			return feature.geometry.coordinates;
		});

	// Use BaseMapbox utility to fit bounds
	// Always have at least 100px padding on all sides
	BaseMapbox.fitMapBounds(this.map, coordinates, {
		padding: { top: 100, bottom: 100, left: 100, right: 100 },
		maxZoom: 15,
		duration: 1000,
		singleMarkerZoom: 14
	});

};

export default MapStandard;
